import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type CSSProperties,
  type RefObject,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronsRight,
  Crown,
  Eye,
  History,
  Pause,
  Play,
  SkipBack,
  SkipForward,
  Volume2,
  VolumeX,
} from 'lucide-react';
import {
  CHECKERS_SIZE,
  deriveCheckers,
  replayStates,
  type CheckersBoard,
  type CheckersMove,
  type CheckersPiece,
  type CheckersState,
  type Coord,
  type GameMeta,
} from '@awwyeah/shared';
import { useCurrentPlayer } from './guestPlayer';
import { legalPathsForPiece } from './checkersOps';
import {
  hopWaypoints,
  isFlippedFor,
  piecesLeft,
  summarizeMove,
  toEngine,
  type CheckersHop,
  type CheckersMoveSummary,
} from './checkersView';
import {
  buzz,
  checkersBoardStyle,
  checkersSoundOn,
  setCheckersBoardStyle,
  type CheckersBoardStyle,
  playMoveSound,
  playPickSound,
  setCheckersSoundOn,
} from './checkersSound';
import './checkers.css';
import { createGameMeta, nowIso } from './gameOps';
import { initialScrubState, scrubReducer } from './replayState';
import { useCheckersMoves } from './useCheckersMoves';
import { useGamesRegistry } from './useGamesRegistry';
import { useGameRoster } from './useHouseholdRoster';
import { SideLine } from './components/SideLine';
import { publishRematch } from './rematch';

const REPLAY_TICK_MS = 800;
/** One square's worth of slide; a jump takes a little longer for its lift. */
const STEP_MS = 200;
const JUMP_MS = 260;

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  } catch {
    return false;
  }
}

function coordKey(c: Coord): string {
  return `${c[0]}-${c[1]}`;
}

function coordEq(a: Coord, b: Coord): boolean {
  return a[0] === b[0] && a[1] === b[1];
}

function pathHasPrefix(seq: readonly Coord[], prefix: readonly Coord[]): boolean {
  if (prefix.length > seq.length) return false;
  return prefix.every((c, i) => coordEq(c, seq[i] as Coord));
}

function pathsEqual(a: readonly Coord[], b: readonly Coord[]): boolean {
  return a.length === b.length && a.every((c, i) => coordEq(c, b[i] as Coord));
}

/**
 * Checkers board (WS-17d) — same move-log/replay/spectator shell as Connect-4's
 * `GameBoardPage` (see `GameBoardPage.tsx`'s kind-dispatch guard), rendered for its own
 * 8x8/diagonal-capture rules instead. Selecting a piece shows its legal destinations
 * (captures exclusively, with a "capture required" hint, whenever any capture is available —
 * the forced-capture rule); a multi-jump is picked one landing at a time and only pushed to
 * the move log once the whole chain is chosen — see `checkersOps.ts`'s `legalPathsForPiece`
 * and the module docblock in `@awwyeah/shared`'s `checkers.ts` for why that's always safe:
 * `deriveCheckers` re-validates the exact path at derive time regardless of what the UI sent.
 *
 * Perspective (owner ask 2026-11-03): each player sees their own pieces at the bottom moving
 * up; spectators see seat 0's view. That's a display-only 180° turn — see `checkersView.ts`.
 * Everything below works in engine coordinates except `CheckersGrid`'s layout loop, which
 * maps each drawn square back with `toEngine`, and the slide animation, which gets its
 * offsets from `hopWaypoints` (display space).
 *
 * The rest of the 2026-11-03 pass is what people praise in turn-based checkers apps: the
 * last move stays marked (with ghosts where pieces were taken) until the next one, the
 * pieces that can move are haloed (the ones that must capture pulse), moves slide and hop,
 * pieces clack (synthesized, mutable), and a captured-pieces tray. No timers, no undo, no
 * nudges — async play stays exactly as relaxed as it was.
 */
export function CheckersBoardPage({
  gameId,
  meta,
}: {
  gameId: string;
  meta: GameMeta;
}): JSX.Element {
  const navigate = useNavigate();
  // `useCurrentPlayer`, not `useUser`: on `/play/:token` the player is a guest with no
  // household session, and this board reads identity itself rather than taking it as a prop.
  const user = useCurrentPlayer();
  const registries = useGamesRegistry();
  const roster = useGameRoster(meta.guestNames);

  const playerIds = meta.playerIds;
  const { movesArray, moves, state } = useCheckersMoves(gameId, playerIds);

  const derive = useCallback(
    (prefix: readonly CheckersMove[]) => deriveCheckers(prefix, playerIds),
    [playerIds],
  );
  const frames = useMemo(() => replayStates(moves, derive), [moves, derive]);

  const [scrub, dispatchScrub] = useReducer(scrubReducer, initialScrubState);
  const [selection, setSelection] = useState<{ from: Coord; pendingPath: Coord[] } | null>(null);

  const prevMoveCountRef = useRef(moves.length);
  useEffect(() => {
    if (moves.length > prevMoveCountRef.current) dispatchScrub({ type: 'new-move-arrived' });
    prevMoveCountRef.current = moves.length;
  }, [moves.length]);

  useEffect(() => {
    if (scrub.mode !== 'scrubbing' || !scrub.playing) return undefined;
    const timer = setTimeout(
      () => dispatchScrub({ type: 'tick', maxFrame: frames.length - 1 }),
      REPLAY_TICK_MS,
    );
    return () => clearTimeout(timer);
  }, [scrub, frames.length]);

  const displayedState = scrub.mode === 'scrubbing' ? (frames[scrub.frame]?.state ?? state) : state;
  // The frame on screen — live is the last one. (Reading `scrub.frame` while live gives 0,
  // which is why the last-move marks used to show only inside the scrubber.)
  const displayIndex = scrub.mode === 'scrubbing' ? scrub.frame : frames.length - 1;
  const shownFrame = frames[displayIndex];
  const lastSummary = useMemo(
    () => summarizeMove(shownFrame?.move, frames[displayIndex - 1]?.state, shownFrame?.state),
    [frames, displayIndex, shownFrame],
  );

  const viewerSeat: 0 | 1 | null =
    user && playerIds[0] === user.id ? 0 : user && playerIds[1] === user.id ? 1 : null;
  const flipped = isFlippedFor(viewerSeat);
  const [soundOn, setSoundOn] = useState(checkersSoundOn);
  const [boardStyle, setBoardStyle] = useState<CheckersBoardStyle>(checkersBoardStyle);
  const boardRef = useRef<HTMLDivElement>(null);

  // Play out the move that just arrived — live (yours or theirs) or a replay stepping
  // forward one frame — as a slide/hop plus its clacks. Jumps of more than one frame (opening
  // the page, scrubbing, back-to-live from far away) just show the result. Layout effect so
  // the disc never flashes at its destination before the slide starts.
  const prevDisplayIndexRef = useRef(displayIndex);
  useLayoutEffect(() => {
    const prev = prevDisplayIndexRef.current;
    prevDisplayIndexRef.current = displayIndex;
    if (displayIndex !== prev + 1 || !lastSummary) return;
    const hops = hopWaypoints(lastSummary.from, lastSummary.path, flipped);
    const capturing = lastSummary.captured.length > 0;
    const hopMs = capturing ? JUMP_MS : STEP_MS;
    playMoveSound({
      hops: hops.length,
      captures: capturing ? hops.length : 0,
      crowned: lastSummary.crowned,
      hopMs,
    });
    if (user && shownFrame?.move?.playerId === user.id) buzz(capturing ? [14, 50, 14] : 14);
    if (prefersReducedMotion()) return;
    animateMove(boardRef.current, lastSummary, hops, hopMs);
    // Only a change of frame should play a move out, not every re-render of the same one.
  }, [displayIndex]);

  // Clear any in-progress piece selection whenever a move actually lands or the scrubber
  // moves to a different frame — a stale selection pointing at a now-wrong square would be
  // confusing, never dangerous (nothing's written until a full path is chosen). Keyed on
  // `moves.length`/`scrub.frame` (stable primitives), NOT on `displayedState.board` itself —
  // `deriveCheckers` builds a fresh board array on every call, so a reference-identity key
  // would fire (and wipe the selection) on every render, not just on real changes.
  useEffect(() => {
    setSelection(null);
  }, [moves.length, scrub.frame]);

  // Mirror a decided game onto the registry meta so the Apps tab's Finished archive has
  // something to filter on. Never a guest: `games` is the one doc a guest may never open
  // (WS-23), so a guest winner leaves the stamp to the household member's own client.
  useEffect(() => {
    if (!meta || !user) return;
    if (!meta.playerIds.includes(user.id) || user.isGuest) return;
    if (meta.status === 'done') return;
    if (!state.winner && !state.isDraw) return;
    const winnerId = state.winner !== null ? meta.playerIds[state.winner] : undefined;
    const at = nowIso();
    registries.map.set(meta.id, {
      ...meta,
      status: 'done',
      ...(winnerId ? { winnerId } : {}),
      finishedAt: at,
      updatedBy: user.id,
      updatedAt: at,
    });
  }, [meta, user, registries.map, state.winner, state.isDraw]);

  if (!user) return <></>;

  const isPlayer = meta.playerIds.includes(user.id);
  // Rematch writes a new entry to the `games` registry, the one doc a guest may not open
  // (WS-23); members only.
  const canRematch = isPlayer && !user.isGuest;
  const opponentId = meta.playerIds.find((id) => id !== user.id) ?? meta.playerIds[1];
  const opponent = roster.getMember(opponentId);
  const finished = state.winner !== null || state.isDraw;
  const myTurn = isPlayer && state.turn !== null && meta.playerIds[state.turn] === user.id;

  const statusLine = (): string => {
    if (state.winner !== null) {
      const winnerId = meta.playerIds[state.winner];
      if (!isPlayer) return `${roster.getMember(winnerId).name} won`;
      return winnerId === user.id ? 'You won' : `${opponent.name} won`;
    }
    if (state.isDraw) return 'Draw';
    if (state.turn === null) return '';
    const turnId = meta.playerIds[state.turn];
    if (!isPlayer) return `${roster.getMember(turnId).name}'s move`;
    return turnId === user.id ? 'Your move' : `${opponent.name}'s move`;
  };

  const maxFrame = frames.length - 1;
  const canWatchReplay = finished && scrub.mode === 'live' && moves.length > 0;
  const canSeeHistory = !finished && scrub.mode === 'live' && moves.length > 0;
  const frameAt = frames[scrub.frame];
  const canInteract = isPlayer && myTurn && !finished && scrub.mode === 'live';

  const lastFrom = new Set<string>();
  const lastTo = new Set<string>();
  const ghosts = new Map<string, CheckersPiece>();
  if (lastSummary) {
    lastFrom.add(coordKey(lastSummary.from));
    for (const step of lastSummary.path) lastTo.add(coordKey(step));
    for (const { at, piece } of lastSummary.captured) ghosts.set(coordKey(at), piece);
  }

  // Which squares hold a piece the current player could legally act on right now (selectable
  // highlight), and — once a piece is tapped — which squares are its next legal landing(s).
  const selectableFrom = new Set<string>();
  const nextTargets = new Set<string>();
  if (canInteract && !selection) {
    for (let r = 0; r < CHECKERS_SIZE; r += 1) {
      for (let c = 0; c < CHECKERS_SIZE; c += 1) {
        const cell = displayedState.board[r]?.[c];
        if (!cell || cell.player !== state.turn) continue;
        if (legalPathsForPiece(moves, playerIds, user.id, [r, c]).length > 0) {
          selectableFrom.add(coordKey([r, c]));
        }
      }
    }
  }
  const captureTargets = new Set<string>();
  let sequences: Coord[][] = [];
  if (canInteract && selection) {
    sequences = legalPathsForPiece(moves, playerIds, user.id, selection.from);
    const matching = sequences.filter((seq) => pathHasPrefix(seq, selection.pendingPath));
    const at = selection.pendingPath[selection.pendingPath.length - 1] ?? selection.from;
    for (const seq of matching) {
      const next = seq[selection.pendingPath.length];
      if (!next) continue;
      nextTargets.add(coordKey(next));
      if (Math.abs(next[0] - at[0]) === 2) captureTargets.add(coordKey(next));
    }
  }

  const colorFor = (playerIndex: 0 | 1): string =>
    roster.getMember(meta.playerIds[playerIndex]).color;

  const handleTapPiece = (coord: Coord): void => {
    if (!canInteract) return;
    if (selection) {
      // Tapping the lifted piece (where it started, or where it's got to mid-chain) puts it
      // back — nothing has been written yet, so cancelling a half-chosen jump is free.
      const at = selection.pendingPath[selection.pendingPath.length - 1];
      if (coordEq(selection.from, coord) || (at && coordEq(at, coord))) setSelection(null);
      return;
    }
    if (!selectableFrom.has(coordKey(coord))) return;
    playPickSound();
    buzz(6);
    setSelection({ from: coord, pendingPath: [] });
  };

  const handleTapTarget = (coord: Coord): void => {
    if (!canInteract || !selection) return;
    if (!nextTargets.has(coordKey(coord))) return;
    const nextPath = [...selection.pendingPath, coord];
    const completed = sequences.find((seq) => pathsEqual(seq, nextPath));
    if (completed) {
      movesArray.push([{ from: selection.from, path: nextPath, playerId: user.id, at: nowIso() }]);
      setSelection(null);
      return;
    }
    playPickSound();
    setSelection({ from: selection.from, pendingPath: nextPath });
  };

  const toggleBoardStyle = (): void => {
    const next: CheckersBoardStyle = boardStyle === 'wood' ? 'bw' : 'wood';
    setCheckersBoardStyle(next);
    setBoardStyle(next);
  };

  const toggleSound = (): void => {
    setCheckersSoundOn(!soundOn);
    setSoundOn(!soundOn);
  };

  const rematch = (): void => {
    const fresh = createGameMeta({
      kind: meta.kind,
      starterId: user.id,
      starterName: roster.getMember(user.id).name,
      opponentId,
      opponentName: opponent.name,
    });
    publishRematch(registries.map, fresh, meta);
    navigate(`/games/${fresh.id}`);
  };

  const watchReplay = (): void => {
    dispatchScrub({ type: 'start-scrub', frame: 0 });
    dispatchScrub({ type: 'play' });
  };
  const seeHistory = (): void => dispatchScrub({ type: 'start-scrub', frame: maxFrame });
  const backToLive = (): void => dispatchScrub({ type: 'back-to-live' });

  return (
    <div className="flex flex-col gap-4 py-4" data-testid="game-board-page">
      <header className="flex items-center gap-2">
        <button
          type="button"
          aria-label={user?.isGuest ? 'Back to the lounge' : 'Back to Games'}
          className="flex h-11 w-11 items-center justify-center text-fg-3"
          onClick={() => navigate(user?.isGuest ? '/play/lounge' : '/games')}
        >
          <ChevronLeft size={22} />
        </button>
        <div className="min-w-0 flex-1">
          <h2 className="truncate font-display text-xl tracking-tight text-fg">{meta.title}</h2>
          <p
            className="text-sm text-fg-2"
            data-testid="game-status-line"
            data-my-turn={myTurn}
            data-finished={finished}
          >
            {!isPlayer && (
              <span className="mr-1 inline-flex items-center gap-1 text-fg-3">
                <Eye size={13} aria-hidden="true" /> Spectating
              </span>
            )}
            {statusLine()}
          </p>
        </div>
        {canWatchReplay && (
          <button
            type="button"
            data-testid="watch-replay-button"
            aria-label="Watch replay"
            onClick={watchReplay}
            className="flex h-11 shrink-0 items-center gap-1.5 rounded-full border border-line-strong px-3 text-sm font-semibold text-fg"
          >
            <Play size={15} aria-hidden="true" /> Watch replay
          </button>
        )}
        <button
          type="button"
          data-testid="checkers-board-style-toggle"
          aria-label={
            boardStyle === 'wood'
              ? 'Switch to a black and white board'
              : 'Switch to the wooden board'
          }
          onClick={toggleBoardStyle}
          className="flex h-11 w-11 shrink-0 items-center justify-center text-fg-3"
        >
          <span
            aria-hidden="true"
            className="grid h-[18px] w-[18px] grid-cols-2 overflow-hidden rounded-[3px] ring-1 ring-current"
          >
            {boardStyle === 'wood' ? (
              <>
                <span className="bg-white" />
                <span className="bg-black" />
                <span className="bg-black" />
                <span className="bg-white" />
              </>
            ) : (
              <>
                <span className="bg-[#e2bf8a]" />
                <span className="bg-[#613619]" />
                <span className="bg-[#613619]" />
                <span className="bg-[#e2bf8a]" />
              </>
            )}
          </span>
        </button>
        <button
          type="button"
          data-testid="checkers-sound-toggle"
          aria-label={soundOn ? 'Turn piece sounds off' : 'Turn piece sounds on'}
          aria-pressed={soundOn}
          onClick={toggleSound}
          className="flex h-11 w-11 shrink-0 items-center justify-center text-fg-3"
        >
          {soundOn ? <Volume2 size={18} /> : <VolumeX size={18} />}
        </button>
        {canSeeHistory && (
          <button
            type="button"
            data-testid="see-history-button"
            aria-label="See how it went"
            onClick={seeHistory}
            className="flex h-11 shrink-0 items-center gap-1.5 rounded-full px-2 text-xs font-medium text-fg-3"
          >
            <History size={14} aria-hidden="true" /> History
          </button>
        )}
      </header>

      {isPlayer && (
        <SideLine
          color={roster.getMember(user.id).color}
          // The board is always turned so the viewer's own pieces start at the bottom.
          detail="bottom, moving up"
          turnText={statusLine()}
          turnColor={!finished && state.turn !== null ? colorFor(state.turn) : null}
          myTurn={myTurn}
        />
      )}

      {scrub.mode === 'scrubbing' && (
        <div
          className="flex flex-col gap-2 rounded-md border border-line bg-raised p-3"
          data-testid="replay-controls"
        >
          <div className="flex items-center justify-between gap-2">
            <span
              className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.06em] text-fg-3"
              data-testid="replay-badge"
            >
              <History size={13} aria-hidden="true" />
              {finished ? 'Replay' : 'Viewing history'}
            </span>
            <button
              type="button"
              data-testid="back-to-live-button"
              onClick={backToLive}
              className="flex h-9 items-center rounded-full border border-line px-3 text-xs font-semibold text-fg-2"
            >
              {finished ? 'Exit replay' : 'Back to live'}
            </button>
          </div>

          {scrub.newMoveAvailable && (
            <button
              type="button"
              data-testid="new-move-chip"
              onClick={backToLive}
              className="flex h-10 items-center justify-center gap-1.5 rounded-full bg-inverse text-xs font-semibold text-inverse-fg"
            >
              Game moved on — back to live?
            </button>
          )}

          <div className="flex items-center gap-2">
            <button
              type="button"
              aria-label="Step back"
              data-testid="replay-step-back"
              disabled={scrub.frame <= 0}
              onClick={() => dispatchScrub({ type: 'step', delta: -1, maxFrame })}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-line text-fg disabled:opacity-40"
            >
              <SkipBack size={16} />
            </button>
            {scrub.playing ? (
              <button
                type="button"
                aria-label="Pause"
                data-testid="replay-pause"
                onClick={() => dispatchScrub({ type: 'pause' })}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-inverse text-inverse-fg"
              >
                <Pause size={16} />
              </button>
            ) : (
              <button
                type="button"
                aria-label="Play"
                data-testid="replay-play"
                disabled={scrub.frame >= maxFrame}
                onClick={() => dispatchScrub({ type: 'play' })}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-inverse text-inverse-fg disabled:opacity-40"
              >
                <Play size={16} />
              </button>
            )}
            <button
              type="button"
              aria-label="Step forward"
              data-testid="replay-step-forward"
              disabled={scrub.frame >= maxFrame}
              onClick={() => dispatchScrub({ type: 'step', delta: 1, maxFrame })}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-line text-fg disabled:opacity-40"
            >
              <SkipForward size={16} />
            </button>
            <input
              type="range"
              aria-label="Scrub to move"
              data-testid="replay-scrubber"
              min={0}
              max={maxFrame}
              value={scrub.frame}
              onChange={(e) => dispatchScrub({ type: 'scrub-to', frame: Number(e.target.value) })}
              className="h-11 flex-1 accent-fg"
            />
            <button
              type="button"
              aria-label="Jump to end"
              data-testid="replay-jump-end"
              disabled={scrub.frame >= maxFrame}
              onClick={() => dispatchScrub({ type: 'jump-to-end', frame: maxFrame })}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-line text-fg disabled:opacity-40"
            >
              <ChevronsRight size={16} />
            </button>
          </div>

          <p className="text-center text-xs text-fg-3" data-testid="replay-position">
            {scrub.frame === 0
              ? 'Start'
              : `Move ${scrub.frame} of ${maxFrame}${frameAt?.at ? ` · ${new Date(frameAt.at).toLocaleString()}` : ''}`}
          </p>
        </div>
      )}

      {canInteract && displayedState.forcedCapture && (
        <p
          className="rounded-full bg-raised px-3 py-1.5 text-center text-xs font-semibold text-fg"
          data-testid="capture-required-hint"
        >
          Capture required
        </p>
      )}

      <CheckersGrid
        board={displayedState.board}
        boardStyle={boardStyle}
        flipped={flipped}
        boardRef={boardRef}
        colorFor={colorFor}
        lastFrom={lastFrom}
        lastTo={lastTo}
        ghosts={ghosts}
        selection={selection}
        selectableFrom={selectableFrom}
        mustCapture={canInteract && displayedState.forcedCapture}
        nextTargets={nextTargets}
        captureTargets={captureTargets}
        onTapPiece={handleTapPiece}
        onTapTarget={handleTapTarget}
      />

      <CapturedTray
        state={displayedState}
        // The viewer's side (the bottom of the board) first; seat 0's for a spectator.
        seats={flipped ? [0, 1] : [1, 0]}
        nameFor={(seat) =>
          meta.playerIds[seat] === user.id ? 'You' : roster.getMember(meta.playerIds[seat]).name
        }
        colorFor={colorFor}
      />

      {finished && canRematch && scrub.mode === 'live' && (
        <button
          type="button"
          data-testid="rematch-button"
          onClick={rematch}
          className="h-12 rounded-md bg-inverse text-base font-semibold text-inverse-fg"
        >
          Rematch
        </button>
      )}
    </div>
  );
}

/**
 * Slides the just-moved disc in from where it started, through every landing of a
 * multi-jump, lifting it over each piece it takes; each taken piece stays visible until the
 * disc passes over it, then pops. Web Animations on elements `CheckersGrid` already drew at
 * their final positions, so React's render is the truth and an interrupted or unsupported
 * animation (jsdom, old browsers) simply leaves the finished board.
 */
function animateMove(
  boardEl: HTMLElement | null,
  summary: CheckersMoveSummary,
  hops: CheckersHop[],
  hopMs: number,
): void {
  const end = summary.path[summary.path.length - 1];
  if (!boardEl || !end || hops.length === 0) return;
  const slot = boardEl.querySelector<HTMLElement>(`[data-piece-at="${coordKey(end)}"]`);
  if (!slot || typeof slot.animate !== 'function') return;

  const n = hops.length;
  const at = (h: { dRow: number; dCol: number }, scale: number): string =>
    `translate(${h.dCol * 100}%, ${h.dRow * 100}%) scale(${scale})`;
  const keyframes: Keyframe[] = [];
  hops.forEach((hop, i) => {
    const next = hops[i + 1] ?? { dRow: 0, dCol: 0 };
    keyframes.push({ offset: i / n, transform: at(hop, 1), easing: 'ease-in' });
    keyframes.push({
      offset: (i + 0.5) / n,
      transform: at(
        { dRow: (hop.dRow + next.dRow) / 2, dCol: (hop.dCol + next.dCol) / 2 },
        hop.capture ? 1.22 : 1.08,
      ),
      easing: 'ease-out',
    });
  });
  keyframes.push({ offset: 1, transform: at({ dRow: 0, dCol: 0 }, 1) });

  const total = n * hopMs;
  slot.dataset.animating = 'true';
  const anim = slot.animate(keyframes, { duration: total });
  const done = (): void => {
    delete slot.dataset.animating;
  };
  anim.onfinish = done;
  anim.oncancel = done;

  summary.captured.forEach(({ at: square }, k) => {
    const el = boardEl.querySelector<HTMLElement>(`[data-captured-at="${coordKey(square)}"]`);
    if (!el || typeof el.animate !== 'function') return;
    const popAt = (k + 0.5) * hopMs;
    const duration = popAt + 180;
    el.animate(
      [
        { opacity: 1, transform: 'scale(1)' },
        { opacity: 1, transform: 'scale(1)', offset: popAt / duration },
        { opacity: 0, transform: 'scale(1.35)' },
      ],
      { duration },
    );
  });
}

function pieceStyle(color: string): CSSProperties {
  return { '--piece': color } as CSSProperties;
}

function Disc({
  piece,
  color,
  selected = false,
  selectable = false,
  mustCapture = false,
}: {
  piece: CheckersPiece;
  color: string;
  selected?: boolean;
  selectable?: boolean;
  mustCapture?: boolean;
}): JSX.Element {
  return (
    <span
      className="ck-piece"
      style={pieceStyle(color)}
      data-king={piece.king}
      data-selected={selected}
      data-selectable={selectable && !selected}
      data-must-capture={mustCapture && selectable && !selected}
    >
      {piece.king && (
        <span className="ck-crown" aria-hidden="true">
          <Crown size={18} strokeWidth={2.4} fill="currentColor" fillOpacity={0.35} />
        </span>
      )}
    </span>
  );
}

function CheckersGrid({
  board,
  boardStyle,
  flipped,
  boardRef,
  colorFor,
  lastFrom,
  lastTo,
  ghosts,
  selection,
  selectableFrom,
  mustCapture,
  nextTargets,
  captureTargets,
  onTapPiece,
  onTapTarget,
}: {
  board: CheckersBoard;
  boardStyle: CheckersBoardStyle;
  /** Draw the engine board turned 180° (seat 0 and spectators) — see `checkersView.ts`. */
  flipped: boolean;
  boardRef: RefObject<HTMLDivElement>;
  colorFor: (playerIndex: 0 | 1) => string;
  lastFrom: Set<string>;
  lastTo: Set<string>;
  /** Engine squares whose pieces the last move took → what stood there. */
  ghosts: Map<string, CheckersPiece>;
  selection: { from: Coord; pendingPath: Coord[] } | null;
  selectableFrom: Set<string>;
  mustCapture: boolean;
  nextTargets: Set<string>;
  captureTargets: Set<string>;
  onTapPiece: (coord: Coord) => void;
  onTapTarget: (coord: Coord) => void;
}): JSX.Element {
  // Mid-chain, the lifted piece is drawn on the last landing chosen so far, not back where
  // it started — so the board shows the jump as it's being picked.
  const liftedAt: Coord | null = selection
    ? (selection.pendingPath[selection.pendingPath.length - 1] ?? selection.from)
    : null;
  const pendingSteps = new Set<string>();
  if (selection && selection.pendingPath.length > 0) {
    pendingSteps.add(coordKey(selection.from));
    for (const step of selection.pendingPath.slice(0, -1)) pendingSteps.add(coordKey(step));
  }
  const liftedPiece = selection ? (board[selection.from[0]]?.[selection.from[1]] ?? null) : null;

  return (
    <div className="-mx-3 sm:mx-auto sm:w-full sm:max-w-[560px]">
      <div className="ck-frame" data-board-style={boardStyle}>
        <div
          ref={boardRef}
          className="ck-inlay grid aspect-square w-full grid-cols-8 grid-rows-8"
          data-testid="checkers-board"
          data-flipped={flipped}
        >
          {Array.from({ length: CHECKERS_SIZE }, (_, dr) =>
            Array.from({ length: CHECKERS_SIZE }, (_, dc) => {
              // Display position → engine square; everything below is in engine terms.
              const [r, c] = toEngine([dr, dc], flipped);
              const here: Coord = [r, c];
              const dark = (r + c) % 2 === 1;
              const key = coordKey(here);
              const grain = `ck-grain-${'abc'[(dr * 3 + dc * 5) % 3]}`;

              if (!dark) {
                return (
                  <span
                    key={key}
                    className={`ck-light ${grain} aspect-square min-w-0`}
                    data-testid="checkers-cell"
                    data-row={r}
                    data-col={c}
                    data-display-row={dr}
                    data-display-col={dc}
                  />
                );
              }

              const boardCell = board[r]?.[c] ?? null;
              const isOrigin = selection ? coordEq(selection.from, here) : false;
              const isLifted = liftedAt ? coordEq(liftedAt, here) : false;
              // The piece drawn here: the lifted piece moves with the pending chain.
              const shown =
                selection && selection.pendingPath.length > 0
                  ? isLifted
                    ? liftedPiece
                    : isOrigin
                      ? null
                      : boardCell
                  : boardCell;
              const isSelectable = selectableFrom.has(key);
              const isTarget = nextTargets.has(key);
              const isLastTo = lastTo.has(key);
              const isLastFrom = lastFrom.has(key);
              const ghost = ghosts.get(key);
              const highlight = isLastTo ? 'ck-last-to' : isLastFrom ? 'ck-last-from' : '';

              return (
                <button
                  key={key}
                  type="button"
                  data-testid="checkers-cell"
                  data-row={r}
                  data-col={c}
                  data-display-row={dr}
                  data-display-col={dc}
                  data-occupant={boardCell === null ? '' : boardCell.player}
                  data-king={boardCell?.king ?? false}
                  data-selectable={isSelectable}
                  data-target={isTarget}
                  data-last-move={isLastTo || isLastFrom}
                  disabled={!isSelectable && !isTarget && !isOrigin && !isLifted}
                  aria-label={
                    isTarget
                      ? 'Move here'
                      : boardCell
                        ? `${boardCell.king ? 'King' : 'Piece'}${isSelectable ? ', can move' : ''}`
                        : 'Empty square'
                  }
                  onClick={() => (isTarget ? onTapTarget(here) : onTapPiece(here))}
                  className={`ck-dark ${grain} ${highlight} relative flex aspect-square min-w-0 items-center justify-center disabled:cursor-default`}
                >
                  {ghost && !boardCell && (
                    <>
                      <span
                        className="ck-ghost"
                        style={pieceStyle(colorFor(ghost.player))}
                        data-testid="checkers-ghost"
                        aria-hidden="true"
                      />
                      {/* The taken piece itself, invisible except while `animateMove` plays. */}
                      <span
                        className="ck-piece-slot"
                        data-captured-at={key}
                        style={{ opacity: 0 }}
                        aria-hidden="true"
                      >
                        <Disc piece={ghost} color={colorFor(ghost.player)} />
                      </span>
                    </>
                  )}
                  {pendingSteps.has(key) && <span className="ck-path-step" aria-hidden="true" />}
                  {isTarget && (
                    <span
                      className={captureTargets.has(key) ? 'ck-target-ring' : 'ck-target-dot'}
                      aria-hidden="true"
                    />
                  )}
                  {shown && (
                    <span className="ck-piece-slot" data-piece-at={key}>
                      <Disc
                        piece={shown}
                        color={colorFor(shown.player)}
                        selected={isLifted}
                        selectable={isSelectable}
                        mustCapture={mustCapture}
                      />
                    </span>
                  )}
                </button>
              );
            }),
          )}
        </div>
      </div>
    </div>
  );
}

/** Pieces left on the board and a little stack of what each side has taken — the viewer's
 * side first. A fact about this game only, never a running tally across games. */
function CapturedTray({
  state,
  seats,
  nameFor,
  colorFor,
}: {
  state: CheckersState;
  seats: [0 | 1, 0 | 1];
  nameFor: (seat: 0 | 1) => string;
  colorFor: (playerIndex: 0 | 1) => string;
}): JSX.Element {
  const left = piecesLeft(state);
  return (
    <div className="grid grid-cols-2 gap-2" data-testid="captured-tally">
      {seats.map((seat) => {
        const other: 0 | 1 = seat === 0 ? 1 : 0;
        const taken = state.captured[seat];
        return (
          <div
            key={seat}
            className="flex min-w-0 flex-col gap-1.5 rounded-md border border-line bg-surface px-3 py-2"
            data-seat={seat}
          >
            <span className="flex min-w-0 items-center gap-1.5 text-sm text-fg">
              <span
                className="ck-tray-piece shrink-0"
                style={pieceStyle(colorFor(seat))}
                aria-hidden="true"
              />
              <span className="truncate font-semibold">{nameFor(seat)}</span>
              <span className="ml-auto shrink-0 text-xs text-fg-3">{left[seat]} left</span>
            </span>
            <span className="flex min-h-[14px] items-center" aria-label={`captured ${taken}`}>
              {taken === 0 ? (
                <span className="text-xs text-fg-3">captured 0</span>
              ) : (
                Array.from({ length: taken }, (_, i) => (
                  <span
                    key={i}
                    className="ck-tray-piece"
                    style={pieceStyle(colorFor(other))}
                    aria-hidden="true"
                  />
                ))
              )}
            </span>
          </div>
        );
      })}
    </div>
  );
}
